/**
 * sticky-cart-bar.js
 * Shows the sticky bar when the hero CTA scrolls out of view.
 * Switches to "View Cart" state when cart has items.
 * Updates on cart:updated event.
 */

(function () {
  'use strict';

  const bar = document.getElementById('sticky-cart-bar');
  if (!bar) return;

  const heroCta = document.querySelector('[data-hero-primary-cta]');
  const btnText = bar.querySelector('[data-sticky-btn-text]');
  const stickyBtn = bar.querySelector('[data-sticky-cta]');
  const isProduct = bar.dataset.stickyMode === 'product';

  let hasItems = false;

  // ─── Visibility control via IntersectionObserver ──────────────────────────

  if (heroCta) {
    const observer = new IntersectionObserver(
      (entries) => {
        const heroVisible = entries[0].isIntersecting;
        bar.classList.toggle('is-visible', !heroVisible);
        bar.setAttribute('aria-hidden', heroVisible ? 'true' : 'false');
      },
      { threshold: 0, rootMargin: '0px 0px -60px 0px' }
    );
    observer.observe(heroCta);
  } else {
    // No hero CTA — show after 300px scroll. On a product page also stay out
    // of the way while the page's own add-to-cart button is on screen, so the
    // shopper never sees two identical buttons stacked (and gets the viewport
    // back while they're choosing a bundle).
    const mainAtc = isProduct
      ? document.querySelector('[data-product-form] [type="submit"]')
      : null;

    // Measured on each (frame-throttled) scroll rather than via an
    // IntersectionObserver: a jump past the button (anchor, scroll restore)
    // can skip the observer's callback and leave the bar stuck hidden.
    const mainAtcOnScreen = () => {
      if (!mainAtc) return false;
      const r = mainAtc.getBoundingClientRect();
      return r.bottom > 0 && r.top < window.innerHeight;
    };

    let ticking = false;
    const update = () => {
      ticking = false;
      const show = window.scrollY > 300 && !mainAtcOnScreen();
      bar.classList.toggle('is-visible', show);
      bar.setAttribute('aria-hidden', show ? 'false' : 'true');
      bar.inert = !show; // no tabbing into an off-screen button
    };
    const queue = () => {
      if (!ticking) { ticking = true; requestAnimationFrame(update); }
    };

    window.addEventListener('scroll', queue, { passive: true });
    window.addEventListener('resize', queue, { passive: true });
    update();
  }

  // ─── Cart state sync ─────────────────────────────────────────────────────

  document.addEventListener('cart:updated', (e) => {
    const cart = e.detail;
    if (!cart) return;
    hasItems = cart.item_count > 0;
    // On a product page the bar stays an add-to-cart — swapping it to
    // "View Cart" after the first add would remove the buy button from the
    // page just as the shopper might add a second tin.
    if (btnText && !isProduct) {
      btnText.textContent = hasItems ? `View Cart (${cart.item_count})` : 'Shop Now';
    }
  });

  // ─── Click: open cart or go to shop ──────────────────────────────────────

  if (stickyBtn) {
    stickyBtn.addEventListener('click', () => {
      // On a product page, submit the page's own add-to-cart form rather than
      // re-deriving the variant. That form already carries the selected
      // variant and the bundle tier's quantity, and cart.js intercepts its
      // submit — so the bar can never disagree with the main button.
      if (isProduct) {
        const form = document.querySelector('[data-product-form]');
        if (form) {
          if (typeof form.requestSubmit === 'function') {
            form.requestSubmit();
          } else {
            form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
          }
          return;
        }
      }

      if (hasItems) {
        document.dispatchEvent(new CustomEvent('cart:open'));
      } else {
        // Scroll to bundle picker or go to collection
        const bundlePicker = document.getElementById('bundle-picker');
        if (bundlePicker) {
          bundlePicker.scrollIntoView({ behavior: 'smooth' });
        } else {
          window.location.href = '/pages/fuel-catalog';
        }
      }
    });
  }

  // ─── Keep the bar's price in step with the bundle tier ────────────────────
  // The PDP's tier radios rewrite [data-atc-label] to e.g. "Add 3 tins — ₹900".
  // Mirror the price half into the bar so the two never show different totals.
  if (isProduct) {
    const atcLabel = document.querySelector('[data-atc-label]');
    const priceEl = bar.querySelector('[data-sticky-price]');
    if (atcLabel && priceEl) {
      const sync = () => {
        const m = atcLabel.textContent.match(/—\s*(.+)$/);
        if (m) priceEl.textContent = m[1].trim();
      };
      sync();
      new MutationObserver(sync).observe(atcLabel, {
        childList: true,
        characterData: true,
        subtree: true,
      });
    }
  }
})();
